import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { PERSIST_KEYS } from '@treneo/shared/constants';

import type { AuthState } from './types';
import { LoginState } from './types';

type AuthStore = AuthState & {
  setLoggedIn: () => void;
  setLoginState: (loginState: LoginState) => void;
  setLoginError: (error: string | null) => void;
  setRegistrationError: (error: string | null) => void;
  setResetPasswordError: (error: string | null) => void;
  setChangePasswordError: (error: string | null) => void;
  setForgotPasswordError: (error: string | null) => void;
  setIsChangePasswordSuccess: (value: boolean) => void;
  setIsRegistrationSuccess: (value: boolean) => void;
  setIsForgotPasswordSuccess: (value: boolean) => void;
  clearLoginError: () => void;
  clearRegistrationError: () => void;
  clearResetPasswordError: () => void;
  clearChangePasswordError: () => void;
  clearForgotPasswordError: () => void;
  clearIsChangePasswordSuccess: () => void;
  clearIsRegistrationSuccess: () => void;
  clearIsForgotPasswordSuccess: () => void;
  clearAllErrors: () => void;
  logoutAction: () => void;
};

const initialState: AuthState = {
  loginState: LoginState.NOT_LOGGED
};

export const useAuthStore = create<AuthStore>()(
  persist(
    set => ({
      ...initialState,
      setLoggedIn: () => set({ loginState: LoginState.LOGGED, loginError: null }),
      setLoginState: loginState => set({ loginState }),
      setLoginError: loginError => set({ loginError }),
      setRegistrationError: registrationError => set({ registrationError }),
      setResetPasswordError: resetPasswordError => set({ resetPasswordError }),
      setChangePasswordError: changePasswordError => set({ changePasswordError }),
      setForgotPasswordError: forgotPasswordError => set({ forgotPasswordError }),
      setIsChangePasswordSuccess: isChangePasswordSuccess => set({ isChangePasswordSuccess }),
      setIsRegistrationSuccess: isRegistrationSuccess => set({ isRegistrationSuccess }),
      setIsForgotPasswordSuccess: isForgotPasswordSuccess => set({ isForgotPasswordSuccess }),
      clearLoginError: () => set({ loginError: null }),
      clearRegistrationError: () => set({ registrationError: null }),
      clearResetPasswordError: () => set({ resetPasswordError: null }),
      clearChangePasswordError: () => set({ changePasswordError: null }),
      clearForgotPasswordError: () => set({ forgotPasswordError: null }),
      clearIsChangePasswordSuccess: () => set({ isChangePasswordSuccess: false }),
      clearIsRegistrationSuccess: () => set({ isRegistrationSuccess: false }),
      clearIsForgotPasswordSuccess: () => set({ isForgotPasswordSuccess: false }),
      clearAllErrors: () =>
        set({
          loginError: null,
          registrationError: null,
          resetPasswordError: null,
          changePasswordError: null,
          forgotPasswordError: null,
          isChangePasswordSuccess: false,
          isRegistrationSuccess: false,
          isForgotPasswordSuccess: false
        }),
      logoutAction: () =>
        set({
          loginState: LoginState.NOT_LOGGED,
          loginError: null,
          registrationError: null,
          resetPasswordError: null,
          changePasswordError: null,
          forgotPasswordError: null,
          isChangePasswordSuccess: false,
          isRegistrationSuccess: false,
          isForgotPasswordSuccess: false
        })
    }),
    {
      name: PERSIST_KEYS.AUTH,
      partialize: state => ({ loginState: state.loginState })
    }
  )
);

export const setLoggedIn = (): void => useAuthStore.getState().setLoggedIn();

export const getAuthLoginState = (): LoginState => useAuthStore.getState().loginState;

export const clearLoginError = (): void => useAuthStore.getState().clearLoginError();
export const clearRegistrationError = (): void => useAuthStore.getState().clearRegistrationError();
export const clearResetPasswordError = (): void =>
  useAuthStore.getState().clearResetPasswordError();
export const clearChangePasswordError = (): void =>
  useAuthStore.getState().clearChangePasswordError();
export const clearForgotPasswordError = (): void =>
  useAuthStore.getState().clearForgotPasswordError();
export const clearIsChangePasswordSuccess = (): void =>
  useAuthStore.getState().clearIsChangePasswordSuccess();
export const clearIsRegistrationSuccess = (): void =>
  useAuthStore.getState().clearIsRegistrationSuccess();
export const clearIsForgotPasswordSuccess = (): void =>
  useAuthStore.getState().clearIsForgotPasswordSuccess();
